import { AxiosError } from 'axios';
import { Dispatch } from 'react';
import { AnyAction } from 'redux';
import {
  MANUAL_INTAKE_REPORT,
  MANUAL_INTAKE_REPORTS,
  REPORT,
  ReportStatus,
  ReportType,
  UPDATE_USER,
  UpdateReport,
  handleAxiosError,
} from 'src/api';
import { handleErrorToast, handleSuccessToast } from 'src/components/molecules';
import type { ManualIntakeDynamicQuestionsPayload } from 'src/components/pages/ManualIntakeDynamicQuestions/ManualIntakeDynamicQuestions';
import {
  CategoryDisplayType,
  CreateDraftReportDocument,
  CreateDraftReportMutation,
  CreateDraftReportMutationVariables,
  CreateQuestionnaireResponseDocument,
  CreateQuestionnaireResponseMutation,
  CreateQuestionnaireResponseMutationVariables,
  MiReportSource,
  SaveResponseAnswersDocument,
  SaveResponseAnswersMutation,
  SaveResponseAnswersMutationVariables,
  UpdateCaseAttributesDocument,
  UpdateCaseAttributesMutation,
  UpdateCaseAttributesMutationVariables,
  UpdateSubmissionDateDocument,
  UpdateSubmissionDateMutation,
  UpdateSubmissionDateMutationVariables,
} from 'src/graphql';
import { fetcher } from 'src/utils/fetch';
import axios from '../axios';
import * as Types from '../types';
import { REPORT_SOURCE_TYPES } from '../types';

export const fetchDraftReport =
  (reportID: string) => async (dispatch: Dispatch<AnyAction>) => {
    try {
      const result = await axios.get(REPORT(reportID));
      const sealedReport: ReportType = result.data.data;
      // Non-empty fields
      const pkUser = sealedReport.pk?.User;
      const reporterUser = sealedReport.Reporter;
      const resolvedUser = pkUser ?? reporterUser;

      const report: UpdateReport = {
        report: sealedReport.report,
        status: sealedReport.status,
        assigneeName: sealedReport.assigneeName,
        substantiation: sealedReport.substantiation,
        reportedCategory: sealedReport.reportedCategory,
        determinedCategory: sealedReport.determinedCategory,
        cases: sealedReport.Cases,
        User: resolvedUser
          ? {
              id: resolvedUser.id,
              name: resolvedUser.name,
              email: resolvedUser.email ?? '',
              role: resolvedUser.role ?? '',
              eid: resolvedUser.eid ?? '',
              contactEmail: resolvedUser.contactEmail,
              relationshipToCompany: resolvedUser.relationshipToCompany,
            }
          : undefined,
        user: resolvedUser ? String(resolvedUser.id) : undefined,
        miReportSource: sealedReport.miReportSource,
      };
      // Potentially empty fields
      if (sealedReport.category) {
        report.category = sealedReport.category;
      }
      if (sealedReport.sealed.relationshipToCompany) {
        report.relationshipToCompany =
          sealedReport.sealed.relationshipToCompany;
      }
      if (Object.keys(sealedReport.sealed.evidence).length !== 0) {
        report.evidence = sealedReport.sealed.evidence;
      }
      if (sealedReport.sealed.perpetrators.length !== 0) {
        report.perpetrators = sealedReport.sealed.perpetrators;
      }
      if (sealedReport.anonymous) {
        report.anonymous = sealedReport.anonymous;
      }
      if (sealedReport.submittedAt) {
        report.submittedAt = new Date(sealedReport.submittedAt);
      }
      if (sealedReport.Cases?.[0]?.CaseAttribute) {
        const caseAttributes = sealedReport.Cases[0].CaseAttribute;
        report.caseAttributes = {
          readAt: caseAttributes.readAt
            ? new Date(caseAttributes.readAt)
            : null,
          investigatingAt: caseAttributes.investigatingAt
            ? new Date(caseAttributes.investigatingAt)
            : null,
          closedAt: caseAttributes.closedAt
            ? new Date(caseAttributes.closedAt)
            : null,
        };
      }
      if (report.status === ReportStatus.DRAFT) {
        dispatch({
          payload: { currentReport: report },
          type: Types.MCCTypes.FETCH_ONE,
        });
      } else {
        dispatch({
          payload: { currentReport: { status: report.status } },
          type: Types.MCCTypes.FETCH_ONE,
        });
      }
    } catch (error) {
      handleAxiosError(error as AxiosError<{ errors: { message: string }[] }>);
      handleErrorToast({ toastMessage: 'Failed to fetch report' });
    }
  };

export const createEditableReport =
  (
    redirectTo: Function,
    existingReport: boolean = false,
    draftReportFeatureFlagsEnabled: boolean = false,
    isLegacyMode: boolean = false,
  ) =>
  async (dispatch: Dispatch<AnyAction>) => {
    try {
      if (!isLegacyMode) {
        dispatch({
          type: Types.MCCTypes.INIT_LOCAL_DRAFT,
          payload: { isExistingReport: existingReport },
        });
        redirectTo('new');
        return;
      }

      dispatch({
        type: Types.MCCTypes.ADD_REPORT,
      });

      if (draftReportFeatureFlagsEnabled) {
        const source = REPORT_SOURCE_TYPES.MCC;

        const result = await fetcher<
          CreateDraftReportMutation,
          CreateDraftReportMutationVariables
        >(CreateDraftReportDocument, { source })();

        dispatch({
          type: Types.MCCTypes.REPORT_SUCCESS,
          payload: {
            reportID: result.createDraftReport.report.id,
            caseID: result.createDraftReport.caseId,
          },
        });

        redirectTo(result.createDraftReport.report.id);
      } else {
        const report = await axios.post(
          MANUAL_INTAKE_REPORTS,
          existingReport
            ? {
                existingReport: true,
              }
            : undefined,
        );
        dispatch({
          type: Types.MCCTypes.REPORT_SUCCESS,
          payload: {
            reportID: report.data.data.report,
            caseID: report.data.data.case,
          },
        });

        redirectTo(report.data.data.report);
      }
    } catch (error) {
      handleErrorToast({ toastMessage: 'Creating editable report failed' });
      dispatch({
        type: Types.MCCTypes.REPORT_FAILURE,
        payload: { error },
      });
      throw error;
    }
  };

export const initLocalDraft =
  (isExistingReport: boolean, redirectTo: Function) =>
  (dispatch: Dispatch<AnyAction>) => {
    dispatch({
      type: Types.MCCTypes.INIT_LOCAL_DRAFT,
      payload: { isExistingReport },
    });
    redirectTo('new');
  };

export const updateLocalDraft =
  (report: UpdateReport) => (dispatch: Dispatch<AnyAction>) => {
    dispatch({
      type: Types.MCCTypes.UPDATE_LOCAL_DRAFT,
      payload: { currentReport: report },
    });
  };

const removeNullFieldsFromReport = (report: UpdateReport): UpdateReport =>
  Object.entries(report)
    .filter(([_, v]) => v != null)
    .reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {});

export type SaveDraftReportSuccessPayload = {
  reportId: string;
  caseId: string | null;
  responseId?: string;
};

export const saveDraftReport =
  (
    report: UpdateReport | null,
    existingReportID: string | null,
    isExistingReport: boolean,
    onSuccess?: (result: SaveDraftReportSuccessPayload) => void,
    dynamicQuestionsPayload?: ManualIntakeDynamicQuestionsPayload,
    options?: { skipSuccessToast?: boolean },
  ) =>
  async (dispatch: Dispatch<AnyAction>) => {
    try {
      dispatch({ type: Types.MCCTypes.SAVE_DRAFT });

      let reportId = existingReportID;
      let caseId = report?.cases?.length ? report.cases[0].id : null;

      if (!existingReportID || existingReportID === 'new') {
        const result: {
          data: { data: { report: string; case: string } };
        } = await axios.post(
          MANUAL_INTAKE_REPORTS,
          isExistingReport
            ? {
                existingReport: true,
              }
            : undefined,
        );

        reportId = result.data.data.report;
        caseId = result.data.data.case;
      }

      if (!reportId || !report) {
        throw new Error('Report data is required to save draft');
      }
      let responseIdToUse = dynamicQuestionsPayload?.responseId;
      if (dynamicQuestionsPayload?.questionnaireId && !responseIdToUse) {
        const createResult = await fetcher<
          CreateQuestionnaireResponseMutation,
          CreateQuestionnaireResponseMutationVariables
        >(CreateQuestionnaireResponseDocument, {
          questionnaireId: dynamicQuestionsPayload.questionnaireId,
          reportId,
        })();
        responseIdToUse =
          createResult?.createQuestionnaireResponse?.responseId ?? undefined;
      }
      const reportWithRemovedNullFields = removeNullFieldsFromReport(report);
      delete reportWithRemovedNullFields.User;
      await axios.put(MANUAL_INTAKE_REPORT(reportId), {
        ...reportWithRemovedNullFields,
        reportedCategory: reportWithRemovedNullFields.reportedCategory?.id,
        determinedCategory: reportWithRemovedNullFields.determinedCategory?.id,
        ...(dynamicQuestionsPayload &&
          responseIdToUse && {
            questionnaireId: dynamicQuestionsPayload.questionnaireId,
            responseId: responseIdToUse,
          }),
      });
      if (
        dynamicQuestionsPayload &&
        responseIdToUse &&
        dynamicQuestionsPayload.answers.length > 0
      ) {
        await fetcher<
          SaveResponseAnswersMutation,
          SaveResponseAnswersMutationVariables
        >(SaveResponseAnswersDocument, {
          responseId: responseIdToUse,
          answers: dynamicQuestionsPayload.answers,
          reportId,
        })();
      }

      if (reportWithRemovedNullFields.submittedAt) {
        await fetcher<
          UpdateSubmissionDateMutation,
          UpdateSubmissionDateMutationVariables
        >(UpdateSubmissionDateDocument, {
          submittedAt: new Date(reportWithRemovedNullFields.submittedAt),
          report: reportId,
        })();
      }

      if (reportWithRemovedNullFields.caseAttributes && caseId) {
        const dates = reportWithRemovedNullFields.caseAttributes;
        await fetcher<
          UpdateCaseAttributesMutation,
          UpdateCaseAttributesMutationVariables
        >(UpdateCaseAttributesDocument, {
          caseAttributes: {
            caseId,
            readAt: dates.readAt ? new Date(dates.readAt) : null,
            investigatingAt: dates.investigatingAt
              ? new Date(dates.investigatingAt)
              : null,
            closedAt: dates.closedAt ? new Date(dates.closedAt) : null,
            companyCreatedAt: reportWithRemovedNullFields.submittedAt
              ? new Date(reportWithRemovedNullFields.submittedAt)
              : null,
          },
          postSubmission: false,
        })();
      }

      dispatch({
        type: Types.MCCTypes.SAVE_DRAFT_SUCCESS,
        payload: {
          reportID: reportId,
          caseID: caseId,
          ...(responseIdToUse && { responseId: responseIdToUse }),
        },
      });

      if (!options?.skipSuccessToast) {
        handleSuccessToast({ toastMessage: 'Draft saved successfully' });
      }
      onSuccess?.({
        reportId,
        caseId: caseId ?? null,
        responseId: responseIdToUse ?? undefined,
      });
      return {
        reportId,
        caseId,
        responseId: responseIdToUse ?? undefined,
      };
    } catch (error) {
      handleErrorToast({ toastMessage: 'Failed to save draft' });
      dispatch({
        type: Types.MCCTypes.SAVE_DRAFT_FAILURE,
        payload: { error: String(error) },
      });
      throw error;
    }
  };

export const ensureDynamicQuestionsResponse =
  (
    report: UpdateReport | null,
    reportId: string,
    payload: ManualIntakeDynamicQuestionsPayload,
  ) =>
  async (_dispatch: Dispatch<AnyAction>): Promise<string | null> => {
    if (!report || !reportId) return null;
    if (payload.responseId) return payload.responseId;
    if (!payload.questionnaireId) return null;
    try {
      let responseIdToUse: string | undefined;
      const createResult = await fetcher<
        CreateQuestionnaireResponseMutation,
        CreateQuestionnaireResponseMutationVariables
      >(CreateQuestionnaireResponseDocument, {
        questionnaireId: payload.questionnaireId,
        reportId,
      })();
      responseIdToUse =
        createResult?.createQuestionnaireResponse?.responseId ?? undefined;
      if (!responseIdToUse) return null;
      const reportWithRemovedNullFields = removeNullFieldsFromReport(report);
      delete reportWithRemovedNullFields.User;
      await axios.put(MANUAL_INTAKE_REPORT(reportId), {
        ...reportWithRemovedNullFields,
        reportedCategory: reportWithRemovedNullFields.reportedCategory?.id,
        determinedCategory: reportWithRemovedNullFields.determinedCategory?.id,
        questionnaireId: payload.questionnaireId,
        responseId: responseIdToUse,
      });
      if (payload.answers.length > 0) {
        await fetcher<
          SaveResponseAnswersMutation,
          SaveResponseAnswersMutationVariables
        >(SaveResponseAnswersDocument, {
          responseId: responseIdToUse,
          answers: payload.answers,
          reportId,
        })();
      }
      return responseIdToUse;
    } catch (error) {
      handleErrorToast({
        toastMessage: 'Failed to prepare form response',
      });
      return null;
    }
  };

export const updateReport =
  (reportID: string, report: UpdateReport) =>
  async (dispatch: Dispatch<AnyAction>) => {
    try {
      dispatch({
        type: Types.MCCTypes.UPDATE,
      });
      const reportWithRemovedNullFields = removeNullFieldsFromReport(report);
      delete reportWithRemovedNullFields.User; // Not needed for backend
      const updatedReport = await axios.put(MANUAL_INTAKE_REPORT(reportID), {
        ...reportWithRemovedNullFields,
        reportedCategory: reportWithRemovedNullFields.reportedCategory?.id,
        determinedCategory: reportWithRemovedNullFields.determinedCategory?.id,
      });
      dispatch({
        type: Types.MCCTypes.UPDATE_SUCCESS,
        payload: {
          reportID: updatedReport.data.data.report,
          currentReport: report,
        },
      });

      handleSuccessToast({
        toastMessage: 'Report has been successfully updated',
      });
    } catch (error) {
      handleErrorToast({ toastMessage: 'Report update failed' });
      dispatch({
        type: Types.MCCTypes.UPDATE_FAILURE,
        payload: { error },
      });
      throw error;
    }
  };

export const updateCurrentEvent =
  (event: Types.MCCEventType, key?: string, value?: unknown) =>
  async (dispatch: Dispatch<AnyAction>) => {
    try {
      const updatedEvent =
        key && value !== undefined && value !== null
          ? { ...event, [key]: value }
          : event;
      dispatch({
        type: Types.MCCTypes.EDIT_EVENT,
        payload: { currentEvent: updatedEvent },
      });
    } catch (error) {
      handleErrorToast({ toastMessage: 'Could not edit event' });
    }
  };

export const resetCurrentReport =
  () => async (dispatch: Dispatch<AnyAction>) => {
    dispatch({ type: Types.MCCTypes.RESET });
  };

export const saveContactEmail =
  (userId: string | undefined, contactEmail: string) =>
  async (dispatch: Dispatch<AnyAction>) => {
    if (!contactEmail || !contactEmail.length) {
      dispatch({
        payload: { error: 'Contact email is required.' },
        type: Types.MCCTypes.SAVE_EMAIL_FAILURE,
      });
      return;
    }

    if (!userId || !userId.length) {
      dispatch({
        payload: {
          error: 'Reporter must be selected and must be an employee.',
        },
        type: Types.MCCTypes.SAVE_EMAIL_FAILURE,
      });

      return;
    }

    try {
      await axios.patch(UPDATE_USER(userId), {
        contactEmail,
        id: userId,
      });

      dispatch({
        payload: { contactEmail },
        type: Types.MCCTypes.SAVE_EMAIL,
      });
      handleSuccessToast({ toastMessage: 'Notifications email updated' });
    } catch (error: unknown) {
      const err = error as {
        response: {
          status: number;
          data: {
            errors: { message: string }[];
          };
        };
      };
      if (err.response.status.toString().startsWith('5')) {
        dispatch({
          payload: { error: 'Internal Error. Try again later.' },
          type: Types.MCCTypes.SAVE_EMAIL_FAILURE,
        });
        return;
      }
      const {
        data: {
          errors: [{ message }],
        },
      } = err.response;

      dispatch({
        payload: { error: message },
        type: Types.MCCTypes.SAVE_EMAIL_FAILURE,
      });
      handleErrorToast({ toastMessage: 'Notifications email update failed' });
    }
  };

export const setReportedCategory =
  (reportedCategory?: CategoryDisplayType) =>
  async (dispatch: Dispatch<AnyAction>) => {
    dispatch({
      payload: { reportedCategory },
      type: Types.MCCTypes.ADD_REPORTED_CATEGORY,
    });
  };

export const setDeterminedCategory =
  (determinedCategory?: CategoryDisplayType) =>
  async (dispatch: Dispatch<AnyAction>) => {
    dispatch({
      payload: { determinedCategory },
      type: Types.MCCTypes.ADD_DETERMINED__CATEGORY,
    });
  };

export const setMiReportSource =
  (miReportSource?: MiReportSource) => (dispatch: Dispatch<AnyAction>) => {
    dispatch({
      payload: { miReportSource },
      type: Types.MCCTypes.ADD_MI_REPORT_SOURCE,
    });
  };
